/**
 * Drawing the wood, the stone and the ore as things that stand in the world.
 *
 * The simulation's half is `src/sim/systems/nodes.ts`: a node is a real object
 * with a tile, a kind and an amount, it is hashed, it is saved, and it is
 * *removed* when the last of it is carried away. This is the picture of that,
 * and like every other renderer file in this project it can only read.
 *
 * ── Why the stage comes from the sim and the silhouette does not ────────────
 *
 * `nodeStage(fraction)` is the simulation's word for how worked-out a node is —
 * `full`, `worked`, `spent` — and the art has a picture for each, so a stand of
 * trees visibly thins as it is cut. That has to come from the sim, because it
 * is a fact about the world.
 *
 * *Which* tree it is must not. A node carries no variant field and should not:
 * it would be a byte in the hash and in every save for a decision no rule ever
 * reads. So the silhouette is a hash of the node's **tile**, which is already
 * hashed state and never changes for the life of the node. A forest is six
 * trees rather than one stamped four hundred times, at a cost of zero bytes,
 * and it survives a save, a reload and a replay because the tile does.
 *
 * Keyed on the tile rather than on the slot index deliberately: slots are
 * recycled, so a node felled and another seeded into the same slot elsewhere
 * would inherit its predecessor's tree. The ground does not move.
 *
 * ── `gone` is authored and unreachable ──────────────────────────────────────
 *
 * The generator bakes four stages and the sim can only ever show three. A node
 * that reaches zero is deleted and its terrain reverts, so there is no object
 * left to hang a `gone` sprite on — the scar belongs to the ground, and
 * `TileMap.stain` is already that layer and already saved. The fourth stage is
 * left in the atlas rather than deleted because that is where it will come
 * from when the stain layer learns to draw it.
 */

import { NodeKind, type NodeKindId } from '../data/nodes.ts';
import { NodeStage, type NodeStageId } from '../sim/systems/nodes.ts';
import { hash2 } from '../core/math.ts';

const SALT_NODE = 0x3d71;

/**
 * The silhouettes each kind may wear.
 *
 * These legacy raster names remain available to tools and asset tests. The
 * live renderer uses cached geometry in scenery.ts: one substantial mineral
 * outcrop or one grove tree per node, with the same depletion stages.
 */
export const NODE_ART: Record<NodeKindId, readonly string[]> = {
  [NodeKind.Tree]: ['tree_ash_a', 'tree_ash_b', 'tree_ash_c', 'tree_black_a', 'tree_black_b', 'tree_snag_a'],
  [NodeKind.Rock]: ['stone_outcrop_a', 'stone_outcrop_b', 'stone_scatter_a', 'stone_scatter_b'],
  [NodeKind.Ore]: ['iron_seam_a', 'iron_seam_b', 'iron_scatter_a', 'iron_scatter_b'],
};

/** Every stage the generator bakes, including the one nothing can reach. */
export const NODE_STAGES: readonly NodeStageId[] = [
  NodeStage.Full,
  NodeStage.Worked,
  NodeStage.Spent,
  NodeStage.Gone,
];

/**
 * The still this node wears, for this kind, at this stage.
 *
 * `tile` — not the slot index. See the header.
 */
export function nodeArt(kind: number, tile: number, stage: NodeStageId): string {
  const set: readonly string[] = NODE_ART[kind as NodeKindId] ?? NODE_ART[NodeKind.Tree];
  const form = set[(set.length * hash2(tile, kind, SALT_NODE)) | 0];
  return `${form}_${stage}`;
}

/**
 * Where the ground line sits in a node still, as a fraction of its height.
 *
 * The generator authors trees at 128px with `groundY: 116` and rocks at 128
 * with `groundY: 102`. Those disagree, and the tree's is used for both: a rock
 * anchored at its own line sits a little low, which is what a boulder half
 * buried in ash should do anyway, and one anchor for the pass is worth more
 * than four pixels of precision.
 */
export const NODE_GROUND = 116 / 128;

/**
 * How wide to draw a node, in tiles — **per kind**.
 *
 * This is the texture canvas width, including the apron and cast shadow.
 * Mineral bodies sit inside a logical 3×3 working site, below the scale of
 * nearby houses. The deposit footprint and its reserves are independent.
 * Trees retain a one-tile trunk while neighbouring crowns form a grove.
 * SceneryBank and Scene share these values; depletion cuts away the geometry
 * without changing its placement or the texture canvas size.
 */
export const NODE_SIZE: Record<NodeKindId, number> = {
  [NodeKind.Tree]: 4.5,
  [NodeKind.Rock]: 2.2,
  [NodeKind.Ore]: 2.15,
};

/** The size for a kind, with a tree's as the fallback for anything new. */
export function nodeSize(kind: number): number {
  return NODE_SIZE[kind as NodeKindId] ?? NODE_SIZE[NodeKind.Tree];
}
